import { useRef, useState } from 'react'
import { centerCrop, Crop, makeAspectCrop, type ReactCropProps } from 'react-image-crop'

import { cropToFile } from '#/components/FormCall/Inputs/InputImage/Logic/cropToFile'
import { InputImageProps, UseImageCropParams } from '#/components/FormCall/Inputs/InputImage/types'

const resolveAspectRatio = (output?: InputImageProps['output']) => {
  const [width, height] = output?.ratio?.split(':').map(Number) ?? []

  if (!width || !height) return undefined

  return width / height
}

export const useImageCrop = ({
  file,
  onChange,
  output,
  picked,
  setPreview,
}: UseImageCropParams) => {
  const imageRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState<ReactCropProps['crop']>()
  const [cropping, setCropping] = useState(false)

  const aspectLocked = resolveAspectRatio(output)

  const handleImageLoad = () => {
    const image = imageRef.current

    if (!image?.width || !image.height) return

    const { height, width } = image

    const aspectNumber = aspectLocked ?? width / height

    const center = centerCrop(
      makeAspectCrop({ unit: '%', width: 100 }, aspectNumber, width, height),
      width,
      height,
    )

    setCrop(center)

    if (picked) {
      setCropping(true)
      saveCrop(center)
    }
  }

  const saveCrop = async (value?: Crop) => {
    const image = imageRef.current

    value = value ?? crop

    if (!image || !file || !value) return

    const cropped = await cropToFile({ crop: value, file, image, output, picked })

    if (cropped) {
      setPreview(cropped)
      onChange?.(cropped)
    }
  }

  const startCropping = () => setCropping(true)
  const endCropping = () => setCropping(false)

  return {
    aspectLocked,
    crop,
    cropping,
    endCropping,
    imageRef,
    onCropChange: setCrop,
    onCropComplete: saveCrop,
    onImageLoad: handleImageLoad,
    saveCrop,
    startCropping,
  }
}
